// 도감 2.0 장부(C6 1단계, 계약 docs/prd/c6-codex-1-contract.md 2·3절, 키 dungeonhero.codex.v1 — 캐릭터별 진행 키, account/keys.ts SYNC_KEYS).
// 형식 { v:1, k: {칸 id: 처치 수}, r: [반영한 영수증 id 최근 256], n: 반영 영수증 총수, t: 마지막 반영 시각 ms }.
// 몬스터·보스 처치 수만 저장한다. 세트·펫·유니크·아바타는 원천(장비 장부·모험·아바타)에서 매번 다시 센다.
// 규칙(AdventureStore와 같은 보존 규칙):
//  - 쓰기 전에 다시 읽고 합친다(처치 수는 칸별 최댓값, r은 합집합 뒤 최근 256, n·t는 최댓값). 두 탭이 서로의 처치 수를 덮어쓰지 않는다.
//  - 읽기 예외(저장소 차단)는 읽기 전용으로 두고 덮어쓰지 않는다. 깨진 원문은 .bak에 남기고 빈 장부로 시작한다.
//  - 미래 판본(v > 1)은 읽기만 한다(쓰지 않는다 — 롤백 안전). 이전 빌드는 이 키를 모른다.
//  - 영수증 id가 r에 있으면 settle은 아무것도 하지 않는다(이중 반영 0). 쓰기에 실패하면 메모리에 남겨 다음 쓰기에 합쳐 다시 시도한다.
import { CODEX_KILL_IDS } from '../shared/codex';
import { CODEX_CELL_ID } from '../shared/codex/syncMerge';
import type { SaveStorage } from './save';

export const CODEX_KEY = "dungeonhero.codex.v1";
/** 기억하는 반영 영수증 수(최근 순) */
export const CODEX_RECEIPT_CAP = 256;
/** 칸 하나의 처치 수 상한(서버 검사와 같다) */
export const CODEX_KILL_MAX = 10_000_000;
const RECEIPT_MAX_LEN = 96;
/** 쓰기 실패 뒤 쉬는 flush 호출 수 */
const FLUSH_RETRY_CALLS = 120;

/** s0 = 서버가 처음 받아들인 시각(ms) — 서버가 찍는 필드라 클라이언트는 만들지 않고, 받은 값을 보존해 다시 쓴다 */
export interface CodexData { k: Record<string, number>; r: string[]; n: number; t: number; s0?: number }
const empty = (): CodexData => ({ k: {}, r: [], n: 0, t: 0 });

/** settle 한 칸의 증가량 */
export interface CodexGain { readonly id: string; readonly n: number }

export class CodexStore {
  private data: CodexData = empty();
  private readonly_ = false;
  /** 저장소에 아직 없는 변경이 메모리에 있다(settle 직후 또는 쓰기 실패) */
  private dirty = false;
  /** 쓰기에 실패한 뒤 쉬어야 하는 flush 호출 수(렌더 프레임마다 한 번 불리므로 약 2초). 같은 실패를 매 프레임 다시 시도하지 않게 */
  private wait = 0;
  /** 바뀔 때마다 1 오른다(점수·뷰 다시 계산) */
  revision = 0;

  constructor(private readonly storage: SaveStorage) { this.read(); }

  /** 칸별 처치 수(읽기 전용) */
  get kills(): Readonly<Record<string, number>> { return this.data.k; }
  killsOf(id: string): number { return this.data.k[id] ?? 0; }
  /** 반영한 영수증 총수(단조 증가) */
  get settled(): number { return this.data.n; }
  /** 마지막 반영 시각(ms, 단조) */
  get lastAt(): number { return this.data.t; }
  /** 반영한 영수증 id(최근 256개, 오래된 순) */
  get receipts(): readonly string[] { return this.data.r; }
  /** 읽기 전용(저장소 읽기 실패·미래 판본)이라 쓰지 않는가 */
  get readOnly(): boolean { return this.readonly_; }
  /** 저장소에 아직 없는 변경이 있는가 */
  get unsaved(): boolean { return this.dirty; }
  hasReceipt(id: string): boolean { return this.data.r.includes(id); }

  /** 저장소를 다시 읽어 메모리와 합친다. 읽기 예외면 메모리를 그대로 두고 읽기 전용이 된다 */
  read(): void {
    let raw: string | null;
    try { raw = this.storage.getItem(CODEX_KEY); } catch { this.readonly_ = true; return; }
    const p = raw ? parse(raw) : null;
    // 키는 있는데 깨졌으면 원문을 .bak에 남기고 빈 장부로 시작한다(처치 기록을 조용히 잃지 않게)
    if (raw && !p) { try { this.storage.setItem(CODEX_KEY + ".bak", raw); } catch { this.readonly_ = true; return; } }
    this.readonly_ = p?.future === true;
    const merged = p ? merge(this.data, p.data) : this.data;
    if (changed(this.data, merged)) this.revision++;
    this.data = merged;
  }

  private write(): boolean {
    if (this.readonly_) return false;
    try { this.storage.setItem(CODEX_KEY, JSON.stringify(this.data.s0 === undefined ? { v: 1, k: this.data.k, r: this.data.r, n: this.data.n, t: this.data.t } : { v: 1, k: this.data.k, r: this.data.r, n: this.data.n, t: this.data.t, s0: this.data.s0 })); this.dirty = false; return true; }
    catch { this.dirty = true; return false; }
  }

  /**
   * 저장소에 없는 변경을 쓴다. 고정 스텝 밖(GameSim.flushSaves — 렌더 프레임·pagehide·마을 진입)에서 부른다: settle은 메모리만 바꾸고
   * 직렬화·저장소 쓰기를 고정 스텝 밖으로 미룬다(skillSlots·gear.flushIdle과 같은 방식). 쓰기에 실패하면 FLUSH_RETRY_CALLS번 쉰 뒤 다시 시도한다(force면 바로).
   * 쓸 것이 없거나 썼으면 true
   */
  flush(force = false): boolean {
    if (!this.dirty) return true;
    if (!force && this.wait > 0) { this.wait--; return false; }
    this.read();
    if (this.write()) { this.wait = 0; return true; }
    this.wait = FLUSH_RETRY_CALLS;
    return false;
  }

  /**
   * 영수증 하나를 반영한다. 이미 반영한 영수증이면 false(아무것도 바꾸지 않음), 새로 반영했으면 true.
   * 읽기 전용이면 false(반영하지 못했다 — 영수증 id도 남기지 않으므로 나중에 같은 영수증으로 다시 시도할 수 있다).
   * gains의 id는 레지스트리의 몬스터·보스 칸만 받는다(그 밖은 버린다). 메모리만 바꾸고 저장은 flush()가 한다(쓰기가 실패해도 메모리에 남아 다시 시도한다).
   */
  settle(receiptId: string, gains: readonly CodexGain[], nowMs = 0): boolean {
    if (typeof receiptId !== "string" || receiptId.length === 0 || receiptId.length > RECEIPT_MAX_LEN) return false;
    this.read();
    if (this.readonly_ || this.data.r.includes(receiptId)) return false;
    const at = nowMs > 0 && Number.isFinite(nowMs) ? Math.floor(nowMs) : 0;
    const next: CodexData = { k: { ...this.data.k }, r: [...this.data.r, receiptId].slice(-CODEX_RECEIPT_CAP), n: this.data.n + 1, t: Math.max(this.data.t, at) };
    if (this.data.s0 !== undefined) next.s0 = this.data.s0;
    for (const g of gains) {
      if (!CODEX_KILL_IDS.has(g.id) || !(g.n > 0)) continue;
      next.k[g.id] = Math.min(CODEX_KILL_MAX, (next.k[g.id] ?? 0) + Math.floor(g.n));
    }
    this.data = next;
    this.dirty = true;
    this.revision++;
    return true;
  }
}

/** 두 장부를 합친다: 처치 수는 칸별 최댓값, 영수증은 합집합(앞쪽 = 오래된 순) 뒤 최근 256, n·t는 최댓값 */
export function mergeCodex(a: CodexData, b: CodexData): CodexData {
  const k: Record<string, number> = {};
  for (const id of Object.keys(a.k)) k[id] = a.k[id];
  for (const id of Object.keys(b.k)) k[id] = Math.max(k[id] ?? 0, b.k[id]);
  const seen = new Set<string>(), r: string[] = [];
  // 저장소(b) 쪽 순서를 먼저, 메모리(a)에만 있는 것을 뒤에 둔다(방금 반영한 것이 가장 최근)
  for (const id of b.r) if (!seen.has(id)) { seen.add(id); r.push(id); }
  for (const id of a.r) if (!seen.has(id)) { seen.add(id); r.push(id); }
  const out: CodexData = { k, r: r.slice(-CODEX_RECEIPT_CAP), n: Math.max(a.n, b.n), t: Math.max(a.t, b.t) };
  // s0는 서버가 정한 불변 값 — 있는 쪽을 보존(둘 다 있으면 더 이른 쪽: 서버 값은 처음 받아들인 시각이라 줄어들지 않는다)
  const s0 = a.s0 !== undefined && b.s0 !== undefined ? Math.min(a.s0, b.s0) : a.s0 ?? b.s0;
  if (s0 !== undefined) out.s0 = s0;
  return out;
}
const merge = mergeCodex;

function changed(a: CodexData, b: CodexData): boolean {
  if (a.n !== b.n || a.t !== b.t || a.s0 !== b.s0 || a.r.length !== b.r.length) return true;
  const ak = Object.keys(a.k), bk = Object.keys(b.k);
  if (ak.length !== bk.length) return true;
  for (const id of bk) if (a.k[id] !== b.k[id]) return true;
  for (let i = 0; i < a.r.length; i++) if (a.r[i] !== b.r[i]) return true;
  return false;
}

function parse(raw: string): { data: CodexData; future: boolean } | null {
  try {
    const v = JSON.parse(raw) as Record<string, unknown>;
    if (!v || typeof v !== "object" || typeof v.v !== "number" || v.v < 1) return null;
    const k: Record<string, number> = {};
    if (v.k && typeof v.k === "object" && !Array.isArray(v.k)) {
      for (const [id, val] of Object.entries(v.k as Record<string, unknown>)) {
        // 레지스트리 밖이라도 칸 id 패턴에 맞는 칸(다음 빌드가 추가한 칸)은 버리지 않고 보존해 다시 쓴다(P3)
        if (CODEX_CELL_ID.test(id) && typeof val === "number" && Number.isFinite(val) && val > 0) k[id] = Math.min(CODEX_KILL_MAX, Math.floor(val));
      }
    }
    const r = Array.isArray(v.r) ? [...new Set((v.r as unknown[]).filter((x): x is string => typeof x === "string" && x.length > 0 && x.length <= RECEIPT_MAX_LEN))].slice(-CODEX_RECEIPT_CAP) : [];
    const n = typeof v.n === "number" && Number.isFinite(v.n) && v.n > 0 ? Math.floor(v.n) : 0;
    const t = typeof v.t === "number" && Number.isFinite(v.t) && v.t > 0 ? Math.floor(v.t) : 0;
    const data: CodexData = { k, r, n, t };
    if (typeof v.s0 === "number" && Number.isSafeInteger(v.s0) && v.s0 > 0) data.s0 = v.s0;
    return { data, future: v.v > 1 };
  } catch { return null; }
}
